import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { logout, getCurrentUser, getMyTelegram, newTelegramLinkToken } from '../services/api';
import { BRAND_NAME } from '../branding';
import { showSuccess, showError } from '../components/ToastNotifications';
import './Login.css';
import './TelegramLinkRequired.css';

// Compulsory Telegram link (operator rule 2026-10-30): App.js renders ONLY this
// page while the signed-in person's telegramLinkRequired flag is set — the
// server also refuses every other API call (403 TELEGRAM_LINK_REQUIRED) until
// the person has pressed Start in the company bot. The page polls
// GET /api/me/telegram every 3 s; when `linked` it re-reads /api/me and lets
// App.js continue.
const POLL_MS = 3000;

function formatCountdown(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function TelegramLinkRequired({ user, setUser }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | ready | off | error
  const [info, setInfo] = useState(null); // { token, bot_username, deep_link, expires_at }
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const finishedRef = useRef(false);
  const seqRef = useRef(0);
  const aliveRef = useRef(true);

  // Linked: re-read /api/me so App.js drops the gate.
  const finish = useCallback(async () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    try {
      const fresh = await getCurrentUser();
      showSuccess('Telegram linked');
      setUser(fresh);
      navigate('/dashboard');
    } catch (err) {
      finishedRef.current = false;
    }
  }, [navigate, setUser]);

  // Fresh one-time code (replaces any pending one on the server). Only the
  // latest request may update the screen.
  const requestToken = useCallback(async () => {
    const seq = ++seqRef.current;
    setBusy(true);
    try {
      const t = await newTelegramLinkToken();
      if (!aliveRef.current || seq !== seqRef.current) return;
      setInfo(t);
      setStatus('ready');
      setCopied(false);
    } catch (err) {
      if (!aliveRef.current || seq !== seqRef.current) return;
      const code = err.response?.data?.code;
      if (code === 'TELEGRAM_ALREADY_LINKED') { finish(); return; }
      if (code === 'TELEGRAM_LINK_OFF' || err.response?.status === 404) { setStatus('off'); return; }
      setStatus((s) => (s === 'ready' ? s : 'error'));
      showError(err.response?.data?.error || 'Could not get a new code. Try again.');
    } finally {
      if (aliveRef.current && seq === seqRef.current) setBusy(false);
    }
  }, [finish]);

  // Latest callbacks, so the one-time first load below never re-runs (a rerun
  // would mint a second code) when a router hook hands back a new function.
  const finishRef = useRef(finish);
  const requestTokenRef = useRef(requestToken);
  useEffect(() => { finishRef.current = finish; requestTokenRef.current = requestToken; });

  // First load: is the feature on / already linked? then ask for a code.
  useEffect(() => {
    aliveRef.current = true;
    (async () => {
      try {
        const s = await getMyTelegram();
        if (!aliveRef.current) return;
        if (s.linked) { finishRef.current(); return; }
        if (s.enabled === false) { setStatus('off'); return; }
      } catch (err) {
        if (!aliveRef.current) return;
        setStatus('error');
        return;
      }
      requestTokenRef.current();
    })();
    return () => { aliveRef.current = false; };
  }, []);

  // Poll every 3 s while a code is on screen.
  useEffect(() => {
    if (status !== 'ready') return undefined;
    const id = setInterval(() => {
      getMyTelegram()
        .then((s) => { if (s && s.linked) finish(); })
        .catch(() => { /* keep polling */ });
    }, POLL_MS);
    return () => clearInterval(id);
  }, [status, finish]);

  // Countdown tick.
  useEffect(() => {
    if (status !== 'ready') return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [status]);

  const handleSignOut = async () => {
    try { await logout(); } catch (err) { /* best-effort */ }
    setUser(null);
  };

  const startCommand = info ? `/start ${info.token}` : '';
  const copyCommand = async () => {
    try {
      await navigator.clipboard.writeText(startCommand);
      setCopied(true);
      setTimeout(() => { if (aliveRef.current) setCopied(false); }, 2000);
    } catch (err) {
      showError('Could not copy — select the text and copy it by hand.');
    }
  };

  const expiresMs = info?.expires_at ? new Date(info.expires_at).getTime() : NaN;
  const hasExpiry = Number.isFinite(expiresMs);
  const remaining = hasExpiry ? expiresMs - now : null;
  const expired = hasExpiry && remaining <= 0;
  const botName = info?.bot_username ? `@${info.bot_username}` : 'the bot';

  return (
    <div className="login-container">
      <div className="login-box tg-link-box">
        <div className="login-brand">{BRAND_NAME}</div>
        <h1 className="login-title">Link your Telegram</h1>

        {status === 'loading' && (
          <p className="login-subtitle" role="status">Getting your link code…</p>
        )}

        {status === 'off' && (
          <p className="login-subtitle" role="alert">
            Telegram linking is not set up on this site yet. Tell an administrator.
          </p>
        )}

        {status === 'error' && (
          <>
            <p className="login-subtitle" role="alert">Could not reach the server to get a link code.</p>
            <div className="tg-link-actions">
              <button type="button" className="btn btn-primary" onClick={requestToken} disabled={busy}>
                {busy ? 'Trying…' : 'Try again'}
              </button>
            </div>
          </>
        )}

        {status === 'ready' && info && (
          <>
            <p className="login-subtitle">
              Every CRM user is linked to one Telegram account. Press the button, Telegram opens the {BRAND_NAME} bot, press Start, and this page continues by itself.
            </p>

            <div className="tg-link-actions">
              <a
                className="btn btn-primary tg-link-open"
                href={info.deep_link}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open {botName} in Telegram
              </a>
            </div>

            <div className="tg-link-fallback">
              <div className="tg-link-fallback-label">Or send this to {botName}:</div>
              <div className="tg-link-code-row">
                <code className="tg-link-code">{startCommand}</code>
                <button type="button" className="btn btn-outline btn-small" onClick={copyCommand}>
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>

            <p className="tg-link-valid" role="status">
              {expired
                ? 'This code has expired.'
                : hasExpiry
                  ? `Code valid for 3 minutes (${formatCountdown(remaining)} left).`
                  : 'Code valid for 3 minutes.'}
              {' '}
              <button type="button" className="tg-link-newcode" onClick={requestToken} disabled={busy}>
                {busy ? 'Getting…' : 'New code'}
              </button>
            </p>
            <p className="tg-link-waiting" role="status">Waiting for you to press Start in Telegram…</p>
          </>
        )}

        <div style={{ textAlign: 'center', marginTop: 'var(--spacing-lg, 16px)' }}>
          <button type="button" className="btn btn-outline btn-small" onClick={handleSignOut}>
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

export default TelegramLinkRequired;
